import React, { useState, useRef, useEffect } from 'react';
import { Search } from 'lucide-react';
import { CouncilHousingService, CouncilHousingOrganization } from '../services/councilHousingService';

interface CouncilHousingSearchInputProps {
  onSelectOrganization: (registrationNumber: string, organizationName: string) => void;
  initialValue?: string;
  className?: string;
  required?: boolean;
  propertyOwnershipType?: 'council' | 'housing-association' | null;
}

export default function CouncilHousingSearchInput({ 
  onSelectOrganization, 
  initialValue = '',
  className = '',
  required = false,
  propertyOwnershipType = null
}: CouncilHousingSearchInputProps) {
  const [query, setQuery] = useState(initialValue);
  const [suggestions, setSuggestions] = useState<CouncilHousingOrganization[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const suggestionsRef = useRef<HTMLDivElement>(null);
  const searchTimeout = useRef<NodeJS.Timeout>();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const searchOrganizations = async (searchQuery: string) => {
    if (searchQuery.length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    setIsLoading(true);

    try {
      const results = await CouncilHousingService.searchCouncilHousing(searchQuery, propertyOwnershipType);
      setSuggestions(results);
      setShowSuggestions(results.length > 0);
    } catch (error) {
      console.error('Search failed:', error);
      setSuggestions([]);
      setShowSuggestions(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleQueryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setQuery(value);
    
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }

    // Wait 1 second before making API call
    searchTimeout.current = setTimeout(() => {
      searchOrganizations(value);
    }, 1000);
  };

  const handleSelectOrganization = (organization: CouncilHousingOrganization) => {
    setQuery(organization.organisation_name);
    setShowSuggestions(false);
    onSelectOrganization(organization.registration_number, organization.organisation_name);
  };

  return (
    <div className={`relative w-full ${className}`}>
      <input
        type="text"
        value={query}
        onChange={handleQueryChange}
        placeholder="Search for housing association or council..."
        className="w-full p-3 pr-12 rounded-xl bg-white text-gray-900 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#30a86b] focus:border-transparent"
        required={required}
      />
      <button 
        onClick={() => searchOrganizations(query)}
        className="absolute right-2 top-1/2 transform -translate-y-1/2 p-2 bg-[#30a86b] rounded-md hover:bg-[#2a9760] transition-colors"
        disabled={isLoading}
      >
        <Search className="w-5 h-5 text-white" />
      </button>
      
      {showSuggestions && suggestions.length > 0 && (
        <div 
          ref={suggestionsRef}
          className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto"
        >
          {suggestions.map((organization, index) => (
            <div
              key={index}
              className="p-3 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors"
              onClick={() => handleSelectOrganization(organization)}
            >
              <div className="font-medium text-gray-900">
                {organization.organisation_name}
              </div>
              <div className="text-sm text-gray-500">
                Reg: {organization.registration_number}
              </div>
            </div>
          ))}
        </div>
      )}
      
      {isLoading && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg p-3">
          <div className="text-gray-600 text-center flex items-center justify-center gap-2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#30a86b]"></div>
            Searching organizations...
          </div>
        </div>
      )}
    </div>
  );
}